import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { getCalcCopy } from "../lib/calculatorCopy";
import { renderInline } from "../lib/renderInline";

/**
 * ResultExplainer — "Understanding your results" module that slots in
 * below a calculator's result panel to boost dwell time + SEO.
 *
 * Pass either a `slug` (auto-looks up copy from /lib/calculatorCopy)
 * or individual `takeaways` / `glossary` / `nextSteps` arrays.
 *
 * Supported inline syntax in takeaway/nextStep strings:
 *   **bold text**               → <strong>
 *   [[Label|calc-slug]]         → button linking to /calculator/{slug}
 *   [[Label|state:california]]  → button linking to /calculators/state-income-tax/california
 *   [[Label|/states]]           → button linking to any internal path
 */
export const ResultExplainer = ({
  slug,
  takeaways,
  glossary,
  nextSteps,
  testid = "result-explainer",
}) => {
  const copy = slug ? getCalcCopy(slug) : {};
  const t = takeaways ?? copy.takeaways ?? [];
  const g = glossary ?? copy.glossary ?? [];
  const n = nextSteps ?? copy.nextSteps ?? [];
  const [openGlossary, setOpenGlossary] = useState(false);

  if (!t.length && !g.length && !n.length) return null;

  return (
    <section
      className="border-2 border-foreground bg-card p-6 md:p-8 space-y-6"
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between border-b-2 border-foreground pb-3">
        <h3 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
          Understanding your results
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          Plain-English guide
        </span>
      </div>

      {t.length > 0 && (
        <div>
          <h4 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            Key takeaways
          </h4>
          <ul className="space-y-3" data-testid={`${testid}-takeaways`}>
            {t.map((item, i) => (
              <li
                key={i}
                className="flex gap-3 font-body text-base leading-relaxed text-foreground/90"
              >
                <span className="font-mono text-xs text-primary shrink-0 mt-1.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{renderInline(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {n.length > 0 && (
        <div>
          <h4 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            What to do next
          </h4>
          <ul className="space-y-2" data-testid={`${testid}-nextsteps`}>
            {n.map((s, i) => (
              <li
                key={i}
                className="font-body text-base leading-relaxed text-foreground/90 pl-4 border-l-2 border-primary/40"
              >
                {renderInline(s)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {g.length > 0 && (
        <div className="border-t border-foreground/20 pt-4">
          <button
            type="button"
            onClick={() => setOpenGlossary((v) => !v)}
            className="w-full flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground transition-colors"
            data-testid={`${testid}-glossary-toggle`}
            aria-expanded={openGlossary}
          >
            <span>Glossary of terms used</span>
            <ChevronDown
              className={`w-3 h-3 transition-transform ${openGlossary ? "rotate-180" : ""}`}
            />
          </button>
          {openGlossary && (
            <dl className="mt-4 space-y-3" data-testid={`${testid}-glossary`}>
              {g.map(({ term, definition }) => (
                <div
                  key={term}
                  className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-1 md:gap-5 border-b border-foreground/10 pb-3"
                >
                  <dt className="font-heading font-semibold text-sm md:text-base">
                    {term}
                  </dt>
                  <dd className="font-body text-sm text-foreground/80 leading-relaxed">
                    {definition}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </section>
  );
};

/**
 * Common glossary entries shared across most federal calculators — pass
 * one of these directly as the `glossary` prop or merge with calc-specific
 * terms.
 */
export const COMMON_GLOSSARY = {
  effective_rate: {
    term: "Effective tax rate",
    definition:
      "Your total tax divided by your gross income. It's your 'all-in' tax rate — usually much lower than your marginal rate because only part of your income is taxed at the top bracket.",
  },
  marginal_rate: {
    term: "Marginal tax rate",
    definition:
      "The bracket rate that applies to the next dollar you earn. Useful for planning decisions like bonuses, Roth conversions, or extra side income.",
  },
  take_home: {
    term: "Take-home pay",
    definition:
      "Gross income minus total federal tax shown here. Does NOT subtract FICA (Social Security/Medicare), state tax, 401(k) contributions, or health insurance premiums.",
  },
  magi: {
    term: "MAGI (Modified Adjusted Gross Income)",
    definition:
      "Your AGI plus certain deductions added back in. OBBBA's new deductions (tips, overtime, senior, auto-loan) phase out above specific MAGI thresholds.",
  },
  agi: {
    term: "AGI (Adjusted Gross Income)",
    definition:
      "Your total gross income minus specific 'above-the-line' deductions. It's the starting point for calculating your federal tax.",
  },
  standard_deduction: {
    term: "Standard deduction",
    definition:
      "A flat dollar amount subtracted from your AGI, based on your filing status. OBBBA made the near-doubled TCJA standard deduction permanent and added an inflation bump for 2026.",
  },
  filing_status: {
    term: "Filing status",
    definition:
      "Single, Married Filing Jointly (MFJ), Married Filing Separately, or Head of Household. Every bracket, deduction, and phase-out threshold varies by status.",
  },
};
